/* eslint-disable no-underscore-dangle */
import { Chart, registerables } from 'chart.js';
import consumer from 'channels/consumer';
import ApplicationController from './application_controller';

Chart.register(...registerables);

const MAX_DATA_POINTS = 360; // ~1 hour at 10s intervals

// Pending jobs are split into one band per queue, stacked bottom to top in this
// order: the recurring cron rows that always wait for their next run form a flat
// base, then the busiest queues. These are the queues the Procfile workers listen
// on. Colors follow the queue, never its position, in colorblind-safe order.
const QUEUE_COLORS = {
  cronjob: '#c3c2b7',
  default: '#2a78d6',
  slow: '#eb6834',
  searchkick: '#1baf7a',
  mailers: '#eda100',
  billing_serial: '#e87ba4',
  critical: '#008300',
  ai_scribe: '#4a3aa7',
  ai_scribe_gai: '#8c7fd6',
};
// Any other queue, including '(none)' for a job saved without one.
const OTHER_QUEUE_COLOR = '#898781';
const QUEUE_FILL_OPACITY = 'd9'; // 85%: any lighter and neighboring bands blur together for colorblind viewers
const JOB_STATUSES = ['pending', 'working', 'failed'];
const INK = '#212529';
const FAILED_RED = '#dc3545';

export default class extends ApplicationController {
  static targets = [
    'activeUsers',
    'activeAmbikiStaff',
    'roomSessions',
    'teletherapySessions',
    'jobsPending',
    'jobsWorking',
    'jobsFailed',
    'recentVisits',
    'recentErrors',
    'updatedAt',
    'connectionStatus',
    'sessionsChart',
    'jobsChart',
  ];

  initialize() {
    this.timestamps = [];
    this.history = {
      active_users: [],
      active_ambiki_staff: [],
      room_sessions: [],
      teletherapy_sessions: [],
      recent_visits: [],
      recent_errors: [],
      jobs_pending: [],
      jobs_working: [],
      jobs_failed: [],
    };
    this.queueOrder = Object.keys(QUEUE_COLORS);
    this.queueHistory = {};
    this.queueOrder.forEach((queue) => {
      this.queueHistory[queue] = this._emptyQueueSeries(0);
    });

    this.subscription = consumer.subscriptions.create(
      { channel: 'LiveActivityChannel' },
      {
        connected: this._connected.bind(this),
        disconnected: this._disconnected.bind(this),
        received: this.received.bind(this),
      }
    );
  }

  connect() {
    this._initCharts();
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.sessionsChart) this.sessionsChart.destroy();
    if (this.jobsChart) this.jobsChart.destroy();
  }

  _connected() {
    if (this.hasConnectionStatusTarget) {
      this.connectionStatusTarget.textContent = '';
      this.connectionStatusTarget.classList.add('d-none');
    }
  }

  _disconnected() {
    if (this.hasConnectionStatusTarget) {
      this.connectionStatusTarget.textContent = 'Disconnected — reconnecting\u2026';
      this.connectionStatusTarget.classList.remove('d-none');
    }
  }

  received(data) {
    this.activeUsersTarget.textContent = data.active_users;
    this.activeAmbikiStaffTarget.textContent = data.active_ambiki_staff;
    this.roomSessionsTarget.textContent = data.room_sessions;
    this.teletherapySessionsTarget.textContent = data.teletherapy_sessions;
    this.jobsPendingTarget.textContent = data.jobs_pending;
    this.jobsWorkingTarget.textContent = data.jobs_working;
    this.jobsFailedTarget.textContent = data.jobs_failed;
    this.recentVisitsTarget.textContent = data.recent_visits;
    this.recentErrorsTarget.textContent = data.recent_errors;
    this.updatedAtTarget.textContent = data.updated_at;

    this._recordDataPoint(data);
  }

  _recordDataPoint(data) {
    this._recordQueuePoint(data.jobs_by_queue || {});
    this.timestamps.push(data.updated_at);
    this.history.active_users.push(data.active_users);
    this.history.active_ambiki_staff.push(data.active_ambiki_staff);
    this.history.room_sessions.push(data.room_sessions);
    this.history.teletherapy_sessions.push(data.teletherapy_sessions);
    this.history.recent_visits.push(data.recent_visits);
    this.history.recent_errors.push(data.recent_errors);
    this.history.jobs_pending.push(data.jobs_pending);
    this.history.jobs_working.push(data.jobs_working);
    this.history.jobs_failed.push(data.jobs_failed);

    if (this.timestamps.length > MAX_DATA_POINTS) {
      this.timestamps.shift();
      Object.values(this.history).forEach((arr) => arr.shift());
      Object.values(this.queueHistory).forEach((series) => {
        JOB_STATUSES.forEach((status) => series[status].shift());
      });
    }

    this._updateCharts();
  }

  _recordQueuePoint(jobsByQueue) {
    Object.keys(jobsByQueue)
      .filter((queue) => !this.queueHistory[queue])
      .sort()
      .forEach((queue) => this._addQueue(queue));

    this.queueOrder.forEach((queue) => {
      const counts = jobsByQueue[queue] || {};
      JOB_STATUSES.forEach((status) => {
        this.queueHistory[queue][status].push(counts[status] || 0);
      });
    });
  }

  // A queue first seen mid-session had no jobs at the earlier points; its band
  // stacks above the known queues.
  _addQueue(queue) {
    this.queueOrder.push(queue);
    this.queueHistory[queue] = this._emptyQueueSeries(this.timestamps.length);
    if (this.jobsChart) {
      this.jobsChart.data.datasets.push(this._queueBand(queue, this.queueOrder.length - 1));
    }
  }

  _emptyQueueSeries(length) {
    return Object.fromEntries(JOB_STATUSES.map((status) => [status, new Array(length).fill(0)]));
  }

  _initCharts() {
    if (this.hasSessionsChartTarget) {
      const ctx = this.sessionsChartTarget.getContext('2d');
      this.sessionsChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [],
          datasets: [
            this._dataset('Active Users', '#007bff', 'active_users'),
            this._dataset('Ambiki Staff', '#3cceb7', 'active_ambiki_staff'),
            this._dataset('Teletherapy V1', '#dc3545', 'room_sessions'),
            this._dataset('Teletherapy V2', '#fd7e14', 'teletherapy_sessions'),
            this._dataset('Recent Visitors', '#6c757d', 'recent_visits'),
            this._dataset('Errors (1h)', '#e83e8c', 'recent_errors'),
          ],
        },
        options: this._chartOptions({ logarithmic: true }),
      });
    }

    if (this.hasJobsChartTarget) {
      const ctx = this.jobsChartTarget.getContext('2d');
      this.jobsChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [],
          // The lines come first so they draw last, over the bands, and each
          // sits in its own stack so only the queue bands add up. The Pending
          // line traces the top edge of the stack.
          datasets: [
            { ...this._dataset('Pending', INK, 'jobs_pending'), stack: 'pending', tension: 0 },
            { ...this._dataset('Working', INK, 'jobs_working'), stack: 'working', tension: 0, borderDash: [6, 4] },
            { ...this._dataset('Failed', FAILED_RED, 'jobs_failed'), stack: 'failed', tension: 0 },
            ...this.queueOrder.map((queue, position) => this._queueBand(queue, position)),
          ],
        },
        options: this._jobsChartOptions(),
      });
    }
  }

  _queueBand(queue, position) {
    const color = QUEUE_COLORS[queue] || OTHER_QUEUE_COLOR;
    return {
      label: queue,
      data: [],
      stack: 'queues',
      // The first band fills down to the axis, every other band down to the one below it.
      fill: position === 0 ? 'origin' : '-1',
      backgroundColor: `${color}${QUEUE_FILL_OPACITY}`,
      borderColor: '#ffffff', // a 2px gap between bands
      borderWidth: 2,
      pointRadius: 0,
      pointHitRadius: 10,
      tension: 0,
      _queue: queue,
    };
  }

  _dataset(label, color, key) {
    return {
      label,
      data: [],
      borderColor: color,
      backgroundColor: `${color}20`,
      borderWidth: 2,
      fill: false,
      tension: 0.3,
      pointRadius: 0,
      pointHitRadius: 10,
      _key: key,
    };
  }

  _chartOptions({ logarithmic = false } = {}) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        title: { display: false },
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            padding: 20,
            font: { size: 13 },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          titleColor: 'white',
          bodyColor: 'white',
        },
      },
      scales: {
        y: {
          type: logarithmic ? 'logarithmic' : 'linear',
          beginAtZero: !logarithmic,
          grid: { color: 'rgba(0,0,0,0.1)' },
        },
        x: {
          grid: { color: 'rgba(0,0,0,0.05)' },
          ticks: { maxTicksLimit: 15 },
        },
      },
    };
  }

  _jobsChartOptions() {
    const options = this._chartOptions();
    options.scales.y.stacked = true;
    // Hiding a band would leave the band above it nothing to fill down to, so
    // only the Pending, Working, and Failed lines toggle.
    options.plugins.legend.onClick = (event, item, legend) => {
      if (legend.chart.data.datasets[item.datasetIndex]._queue) return;
      Chart.defaults.plugins.legend.onClick(event, item, legend);
    };
    Object.assign(options.plugins.tooltip, {
      filter: (item) => !item.dataset._queue || item.raw > 0,
      itemSort: (a, b) => this._tooltipRank(a) - this._tooltipRank(b) || a.datasetIndex - b.datasetIndex,
      callbacks: {
        label: (item) => `${item.dataset._queue ? '  ' : ''}${item.dataset.label}: ${item.formattedValue}`,
        afterLabel: (item) => {
          const status = { jobs_working: 'working', jobs_failed: 'failed' }[item.dataset._key];
          return status ? this._queueBreakdown(status, item.dataIndex) : [];
        },
      },
    });
    return options;
  }

  // Hover order: Pending, its queue bands, Working, Failed.
  _tooltipRank(item) {
    if (item.dataset._queue) return 1;
    return { jobs_pending: 0, jobs_working: 2, jobs_failed: 3 }[item.dataset._key];
  }

  // A status's non-zero counts per queue at one point, in band order.
  _queueBreakdown(status, index) {
    return this.queueOrder
      .filter((queue) => this.queueHistory[queue][status][index] > 0)
      .map((queue) => `  ${queue}: ${this.queueHistory[queue][status][index].toLocaleString()}`);
  }

  _updateCharts() {
    [this.sessionsChart, this.jobsChart].forEach((chart) => {
      if (!chart) return;

      chart.data.labels = this.timestamps;
      chart.data.datasets.forEach((dataset) => {
        dataset.data = dataset._queue ? this.queueHistory[dataset._queue].pending : this.history[dataset._key];
      });
      chart.update('none');
    });
  }
}
